import { useState, type JSX } from 'react'
import { Button, Calendar, Popover, PopoverContent, PopoverTrigger } from '@ttab/elephant-ui'
import { CalendarIcon } from '@ttab/elephant-ui/icons'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useRegistry } from '@/hooks/useRegistry'
import { parseDate } from '@/shared/datetime'

export interface DateRange {
  from: string
  to: string
}

interface DateRangePickerProps {
  value: DateRange
  onChange: (value: DateRange) => void
}

export const DateRangePicker = ({ value, onChange }: DateRangePickerProps): JSX.Element => {
  const { t } = useTranslation()
  const { locale } = useRegistry()
  const [open, setOpen] = useState(false)

  const from = parseDate(value.from)
  const to = parseDate(value.to)
  const hasRange = !!from || !!to

  const formatter = new Intl.DateTimeFormat(locale.code.full, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })

  const label = hasRange
    ? `${from ? formatter.format(from) : ''} – ${to ? formatter.format(to) : ''}`.trim()
    : t('misc.selectDates')

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={(
          <Button
            variant='outline'
            className='w-full justify-start font-normal text-sm'
          >
            <CalendarIcon size={18} strokeWidth={1.75} className='mr-2 shrink-0' />
            <span className={`truncate ${hasRange ? '' : 'text-muted-foreground'}`}>
              {label}
            </span>
          </Button>
        )}
      />
      <PopoverContent className='w-auto p-0' align='start'>
        <Calendar
          mode='range'
          locale={locale.module}
          selected={hasRange ? { from, to } : undefined}
          defaultMonth={from ?? to}
          onSelect={(range) => {
            onChange({
              from: range?.from ? format(range.from, 'yyyy-MM-dd') : '',
              to: range?.to ? format(range.to, 'yyyy-MM-dd') : ''
            })
          }}
          footer={hasRange
            ? (
                <Button
                  variant='outline'
                  onClick={() => {
                    onChange({ from: '', to: '' })
                    setOpen(false)
                  }}
                >
                  {t('actions.clear')}
                </Button>
              )
            : undefined}
        />
      </PopoverContent>
    </Popover>
  )
}
